import { useEffect, useRef } from "react";
import { useGlobalState } from "./use-global-state";

/**
 * Hook to manage audio playback with automatic pausing of other audio elements
 * Uses the global state pattern based on React Query
 * Manages the audio element internally for simplified usage
 */
export const useAudioPlayer = (audioId: string, audioSrc?: string) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useGlobalState<string | null>(
    "audio-player-current",
    null,
  );

  const isPlaying = currentlyPlaying === audioId;

  // Initialize audio element when src is provided
  useEffect(() => {
    if (audioSrc) {
      audioRef.current = new Audio(audioSrc);
      audioRef.current.onended = () => setCurrentlyPlaying(null);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioSrc]);

  // Pause when another audio starts playing
  useEffect(() => {
    if (!isPlaying && audioRef.current) {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  const play = () => {
    if (audioRef.current) {
      setCurrentlyPlaying(audioId);
      audioRef.current.play();
    }
  };

  const pause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      // Reset to beginning
      audioRef.current.currentTime = 0;
    }
    if (isPlaying) {
      setCurrentlyPlaying(null);
    }
  };

  const toggle = () => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  };

  return { isPlaying, play, pause, toggle, audioRef };
};
